import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, Container, FormControlLabel, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { saveAs } from 'file-saver';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { validateToken } from '../utils/Cookie';

type Catalogo = { arquivo: string; marca: 'LYOR' | 'WOLFF' | 'GERAL'; titulo: string; url: string };

export default function LaminasComerciais() {
  const navigate = useNavigate();
  const [catalogos, setCatalogos] = useState<Catalogo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [downloadStatus, setDownloadStatus] = useState('');
  const downloadController = useRef<AbortController | null>(null);
  const selecionados = catalogos.filter(item => selected.includes(item.arquivo));

  useEffect(() => () => downloadController.current?.abort(), []);

  const toggle = (items: Catalogo[]) => {
    const nomes = items.map(item => item.arquivo);
    setSelected(current => nomes.every(nome => current.includes(nome))
      ? current.filter(nome => !nomes.includes(nome))
      : Array.from(new Set([...current, ...nomes])));
  };

  const baixar = async (items: Catalogo[]) => {
    if (!items.length || downloadController.current) return;
    const controller = new AbortController();
    downloadController.current = controller;
    setDownloading(true);
    setDownloadError('');
    setDownloadStatus(`Preparando ${items.length} ${items.length === 1 ? 'lâmina' : 'lâminas'}…`);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND}/catalogos/download`, {
        arquivos: items.map(item => item.arquivo),
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob', signal: controller.signal,
        onDownloadProgress: event => {
          if (!controller.signal.aborted) setDownloadStatus(`Recebendo ZIP · ${(event.loaded / 1024 / 1024).toFixed(1)} MB`);
        },
      });
      if (controller.signal.aborted) return;
      const marcas = Array.from(new Set(items.map(item => item.marca).filter(marca => marca !== 'GERAL')));
      saveAs(data, `laminas-${marcas.length === 1 ? marcas[0].toLowerCase() : marcas.length === 0 ? 'gerais' : 'lyor-e-wolff'}.zip`);
      setDownloadStatus('ZIP pronto! O download foi enviado ao navegador.');
    } catch (err) {
      if (controller.signal.aborted) return;
      setDownloadStatus('');
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        navigate('/login?logout=true');
      } else {
        setDownloadError('Não foi possível baixar as lâminas. Atualize a lista e tente novamente.');
      }
    } finally {
      if (downloadController.current === controller) {
        downloadController.current = null;
        setDownloading(false);
      }
    }
  };

  useEffect(() => {
    if (!validateToken()) { navigate('/login?logout=true'); return; }
    const controller = new AbortController();
    const carregar = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get<{ catalogos: Catalogo[] }>(`${process.env.REACT_APP_BACKEND}/catalogos`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          signal: controller.signal,
          timeout: 30000,
        });
        if (!controller.signal.aborted) {
          setCatalogos(data.catalogos);
          setSelected(current => current.filter(nome => data.catalogos.some(item => item.arquivo === nome)));
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          navigate('/login?logout=true');
        } else {
          setError('Não foi possível carregar as lâminas comerciais. Tente novamente.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    carregar();
    return () => controller.abort();
  }, [navigate, revision]);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="flex-start" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: '#1a3661', fontWeight: 700, mb: 1 }}>Lâminas comerciais</Typography>
          <Typography color="text.secondary">Consulte os materiais ou selecione suas lâminas para baixar em um único ZIP.</Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => setRevision(value => value + 1)} disabled={loading || downloading} sx={{ flexShrink: 0 }}>Atualizar</Button>
      </Stack>
      {downloadError && <Alert severity="error" onClose={() => setDownloadError('')} sx={{ mb: 2 }}>{downloadError}</Alert>}
      {downloadStatus && <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography role="status" variant="body2">{downloadStatus}</Typography>
          {downloading && <Button size="small" onClick={() => { downloadController.current?.abort(); setDownloadStatus('Download cancelado.'); }}>Cancelar</Button>}
        </Stack>
        {downloading && <LinearProgress sx={{ mt: 1.5, borderRadius: 1 }} />}
      </Paper>}
      {loading ? (
        <Stack alignItems="center" spacing={2} sx={{ py: 8 }} role="status"><CircularProgress /><Typography color="text.secondary">Carregando lâminas…</Typography></Stack>
      ) : error ? (
        <Alert severity="error" action={<Button color="inherit" onClick={() => setRevision(value => value + 1)}>Tentar novamente</Button>}>{error}</Alert>
      ) : (
        <>
        <Paper variant="outlined" sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: '#f8fafc', borderColor: '#dce4ef' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" gap={2}>
            <Box>
              <Typography component="div" sx={{ fontWeight: 600, color: '#1a3661' }}>Sua seleção <Chip size="small" label={selecionados.length} sx={{ ml: 0.5 }} /></Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Combine LYOR e WOLFF no mesmo download.</Typography>
            </Box>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {selecionados.length > 0 && <Button disabled={downloading} onClick={() => setSelected([])}>Limpar</Button>}
              <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} disabled={downloading || !catalogos.length} onClick={() => baixar(catalogos)}>Baixar todas ({catalogos.length})</Button>
              <Button variant="contained" disableElevation startIcon={<DownloadOutlinedIcon />} disabled={downloading || !selecionados.length} onClick={() => baixar(selecionados)} sx={{ bgcolor: '#1a3661', borderRadius: 2 }}>Baixar selecionadas ({selecionados.length})</Button>
            </Stack>
          </Stack>
        </Paper>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {(['LYOR', 'WOLFF'] as const).map(marca => {
            const materiais = catalogos.filter(item => item.marca === marca || item.marca === 'GERAL');
            const quantidadeSelecionada = materiais.filter(item => selected.includes(item.arquivo)).length;
            return (
              <Paper key={marca} component="section" aria-label={`Lâminas ${marca}`} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', alignSelf: 'start' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 3, bgcolor: marca === 'LYOR' ? '#f6f1eb' : '#edf2f7' }}>
                  <Typography component="h2" variant="h5" sx={{ fontWeight: 700, letterSpacing: 3, color: '#1a3661' }}>{marca}</Typography>
                  <Chip label={`${materiais.length} ${materiais.length === 1 ? 'lâmina' : 'lâminas'}`} size="small" sx={{ bgcolor: 'white' }} />
                </Stack>
                {materiais.length > 0 && <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <FormControlLabel sx={{ mr: 1 }} control={<Checkbox size="small" disabled={downloading} checked={quantidadeSelecionada === materiais.length} indeterminate={quantidadeSelecionada > 0 && quantidadeSelecionada < materiais.length} onChange={() => toggle(materiais)} inputProps={{ 'aria-label': `Selecionar todas as lâminas ${marca}` }} />} label={<Typography variant="body2">Selecionar todas</Typography>} />
                  <Button size="small" startIcon={<DownloadOutlinedIcon />} disabled={downloading} onClick={() => baixar(materiais)}>Baixar {marca}</Button>
                </Stack>}
                {materiais.length === 0 ? <Typography sx={{ p: 3 }} color="text.secondary">Nenhuma lâmina disponível no momento.</Typography> : (
                  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                    {materiais.map(item => (
                      <Box component="li" key={item.arquivo} sx={{ display: 'flex', alignItems: 'center', pl: 1, bgcolor: selected.includes(item.arquivo) ? '#f0f5fc' : 'transparent', '& + &': { borderTop: '1px solid', borderColor: 'divider' } }}>
                        <Checkbox disabled={downloading} checked={selected.includes(item.arquivo)} onChange={() => toggle([item])} inputProps={{ 'aria-label': `Selecionar ${item.arquivo}` }} />
                        <Box component="a" href={item.url} target="_blank" rel="noopener noreferrer" aria-label={`Abrir ${item.titulo} — ${marca} (PDF, nova aba)`} sx={{ display: 'flex', flex: 1, minWidth: 0, gap: 2, alignItems: 'center', py: 3, pl: 1, pr: 2, color: 'inherit', textDecoration: 'none', '&:hover': { bgcolor: '#f8fafc' }, '&:focus-visible': { outline: '2px solid #1a3661', outlineOffset: -2 } }}>
                          <PictureAsPdfOutlinedIcon sx={{ color: '#1a3661', fontSize: 30 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, textTransform: 'capitalize', overflowWrap: 'anywhere' }}>{item.titulo.toLocaleLowerCase('pt-BR').replace('colecao', 'coleção')}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{item.arquivo}</Typography>
                          </Box>
                          <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
        </>
      )}
    </Container>
  );
}
